import { ProviderPublicConfig } from 'api/ApiTypes'

const FLOW_KEY_PREFIX = 'sso.v2.flow.'
// localStorage is origin-scoped and every portal runs on its own (sub)domain,
// so a single session key per origin is sufficient.
const SESSION_KEY = 'sso.v2.session'

// Abandoned flows older than this can never complete (the IdP code has long
// expired), so they are safe to purge.
const FLOW_MAX_AGE_MS = 60 * 60 * 1000

export type SsoFlowRecord = {
  state: string
  code_verifier: string
  provider_slug: string
  provider: ProviderPublicConfig
  authorization_endpoint: string
  token_endpoint: string
  contact_id?: string
  return_to?: string
  created_at: number
  status: 'started' | 'consumed'
  attempt: number
}

/**
 * Everything the logout path needs, captured at login time so signing out
 * never depends on legacy localStorage keys or a provider re-fetch.
 */
export type SsoSessionRecord = {
  provider_slug: string
  id_token?: string
  client_id?: string
  logout_uri?: string
  logout_redirect_uri?: string
  skip_login_as_logout?: boolean
}

const flowKey = (state: string) => `${FLOW_KEY_PREFIX}${state}`

const parseRecord = <T>(raw: string | null): T | null => {
  if (!raw) return null

  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export const createFlow = (
  record: Omit<SsoFlowRecord, 'created_at' | 'status'>
): SsoFlowRecord => {
  const flow: SsoFlowRecord = {
    ...record,
    created_at: Date.now(),
    status: 'started'
  }

  localStorage.setItem(flowKey(flow.state), JSON.stringify(flow))

  return flow
}

export const getFlow = (state: string): SsoFlowRecord | null =>
  parseRecord<SsoFlowRecord>(localStorage.getItem(flowKey(state)))

/**
 * Atomically (synchronously, before any await) marks the flow as consumed so
 * a duplicated tab or remount sees `status === 'consumed'` and never posts
 * the single-use authorization code a second time.
 */
export const consumeFlow = (state: string): SsoFlowRecord | null => {
  const flow = getFlow(state)

  if (!flow || flow.status === 'consumed') return null

  const consumed: SsoFlowRecord = { ...flow, status: 'consumed' }

  localStorage.setItem(flowKey(state), JSON.stringify(consumed))

  return consumed
}

export const deleteFlow = (state: string): void => {
  localStorage.removeItem(flowKey(state))
}

export const purgeStaleFlows = (): void => {
  const now = Date.now()
  const staleKeys: string[] = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)

    if (!key?.startsWith(FLOW_KEY_PREFIX)) continue

    const flow = parseRecord<SsoFlowRecord>(localStorage.getItem(key))

    if (!flow?.created_at || now - flow.created_at > FLOW_MAX_AGE_MS) {
      staleKeys.push(key)
    }
  }

  staleKeys.forEach((key) => localStorage.removeItem(key))
}

export const setSsoSession = (session: SsoSessionRecord): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export const getSsoSession = (): SsoSessionRecord | null =>
  parseRecord<SsoSessionRecord>(localStorage.getItem(SESSION_KEY))

export const clearSsoSession = (): void => {
  localStorage.removeItem(SESSION_KEY)
}
